import { cn } from '@fortem-monorepo/js-utilities'
import { createColDefs } from '@fortem-monorepo/react-ag'
import { CustomAgGrid } from '@fortem-monorepo/react-ag'
import { Widget, WidgetContent } from '@fortem-monorepo/react-spa'
import { Flows } from '@fortem-monorepo/web-sdk'

import { Section } from '@/pages/funds/flows/components/Section'
import { StreamStageProps } from '@/pages/funds/flows/components/types'

const toDecimalPlaces = (number: number, decimalPlaces: number = 2) => {
  const factor = 10 ** decimalPlaces
  return Math.trunc(number * factor) / factor
}

const formatPercent = (number: number) => {
  return `${toDecimalPlaces(number * 100)}%`
}

const formatNumber = (number: number) => {
  return toDecimalPlaces(number).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

export interface GoldCtaStageProps extends StreamStageProps<Flows.GetGoldCtaOutput> {}

export const GoldCtaStage: React.FC<GoldCtaStageProps> = ({ output }) => {
  return (
    <div className={'space-y-4'}>
      <Section title="Summary" defaultOpen={true}>
        <div className={'flex gap-10 p-4'}>
          <div>
            <table>
              <tbody>
                <tr>
                  <TableHeader>AUM ({output.fund_currency})</TableHeader>
                  <TableCell>{formatNumber(output.aum_fund)}</TableCell>
                </tr>
                <tr>
                  <TableHeader>New AUM ({output.fund_currency})</TableHeader>
                  <TableCell>{formatNumber(output.new_aum_fund)}</TableCell>
                </tr>
                {output.new_aum_exposure && (
                  <tr>
                    <TableHeader>New AUM ({output.exposure_currency})</TableHeader>
                    <TableCell>{formatNumber(output.new_aum_exposure)}</TableCell>
                  </tr>
                )}
                <tr>
                  <TableHeader>Subscriptions (T-1)</TableHeader>
                  <TableCell>{formatNumber(output.subscriptions_tmo_fund)}</TableCell>
                </tr>
                <tr>
                  <TableHeader>Subscriptions (T)</TableHeader>
                  <TableCell>{formatNumber(output.subscriptions_t_fund)}</TableCell>
                </tr>
              </tbody>
            </table>
          </div>
          <div>
            <table>
              <tbody>
                <tr>
                  <TableHeader>Current {output.type} Allocation (%)</TableHeader>
                  <TableCell>{formatPercent(output.current_allocation_pct)}</TableCell>
                </tr>
                <tr>
                  <TableHeader>Target {output.type} Allocation (%)</TableHeader>
                  <TableCell>{formatPercent(output.target_allocation_pct)}</TableCell>
                </tr>
                <tr>
                  <TableHeader>{output.type} allocation if no action (%)</TableHeader>
                  <TableCell>{formatPercent(output.allocation_pct_if_no_action)}</TableCell>
                </tr>
              </tbody>
            </table>
          </div>
          <div>
            <table>
              <tbody>
                <tr>
                  <TableHeader>Current Allocation ({output.exposure_currency})</TableHeader>
                  <TableCell>{formatNumber(output.current_allocation_exposure)}</TableCell>
                </tr>
                <tr>
                  <TableHeader>Target Notional ({output.exposure_currency})</TableHeader>
                  <TableCell>{formatNumber(output.target_notional_exposure)}</TableCell>
                </tr>
                <tr>
                  <TableHeader>Upsize / Downsize</TableHeader>
                  <TableCell>{formatNumber(output.upsize_downsize_exposure)}</TableCell>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </Section>
      <Section title="Holdings" defaultOpen={true}>
        <Widget exposed>
          <WidgetContent>
            <CustomAgGrid
              rowData={output.holdings}
              columnDefs={holdingsColDefs(output)}
              statusBar={{ statusPanels: [] }}
            />
          </WidgetContent>
        </Widget>
      </Section>
    </div>
  )
}

export interface TableHeaderProps extends React.ComponentProps<'td'> {}

export const TableHeader: React.FC<TableHeaderProps> = ({ children, className, ...props }) => {
  return (
    <td className={cn('text-sm px-4 py-1', className)} {...props}>
      {children}
    </td>
  )
}

export interface TableCellProps extends React.ComponentProps<'td'> {}

export const TableCell: React.FC<TableCellProps> = ({ children, className, ...props }) => {
  return (
    <td className={cn('text-sm text-muted-foreground px-4 py-1', className)} {...props}>
      {children}
    </td>
  )
}

const holdingsColDefs = (data: Flows.GetGoldCtaOutput) => {
  return createColDefs<Flows.GetGoldCtaOutput['holdings'][number]>([
    {
      field: 'unique_identifier',
      headerName: 'Instrument',
      width: 120,
      filter: false,
    },
    {
      field: 'price_type',
      headerName: 'Price Type',
      width: 100,
      filter: false,
    },
    {
      field: 'under_unique_identifier',
      headerName: 'Underlying',
      width: 150,
      filter: false,
    },
    {
      field: 'quantity_notional',
      headerName: 'Notional (Quantity)',
      width: 150,
      filter: false,
      valueFormatter: (params) => {
        return toDecimalPlaces(params.value, 2).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      },
    },
    {
      field: 'notional',
      headerName: 'Notional',
      width: 150,
      filter: false,
      valueFormatter: (params) => {
        return toDecimalPlaces(params.value, 2).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      },
    },
    {
      field: 'px_tmo',
      headerName: 'Price (T-1)',
      width: 120,
      filter: false,
    },
    {
      field: 'trade_tmo',
      headerName: 'Trade (T-1)',
      width: 150,
      filter: false,
      valueFormatter: (params) => {
        return toDecimalPlaces(params.value, 2).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      },
    },
    {
      field: 'mtm',
      headerName: 'MTM',
      filter: false,
      valueFormatter: (params) => {
        return toDecimalPlaces(params.value, 2).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      },
    },
    {
      field: 'total',
      headerName: 'Total ($)',
      filter: false,
      valueFormatter: (params) => {
        return toDecimalPlaces(params.value, 2).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      },
    },
    {
      // hide: data.holdings.every((holding) => !holding.last_reset_date),
      field: 'last_reset_date',
      headerName: 'Last Reset Date',
      filter: false,
      width: 120,
    },
    {
      // hide: data.holdings.every((holding) => !holding.spot_at_last_reset),
      field: 'price_at_last_reset',
      headerName: 'Price @ Last Reset',
      filter: false,
      width: 150,
    },
    {
      hide: data.holdings.every((holding) => holding.price_type == 'notional'),
      field: 'spot_at_effective_date',
      headerName: 'Spot @ Effective Date',
      filter: false,
      width: 150,
    },
  ])
}
